<template>
  <div v-if="visible && note" class="modal-overlay">
    <div class="modal-content">
      <button class="close-button" @click="closeModal" :disabled="isUpdating">×</button>

      <h2 class="modal-title">Move Note to Folder</h2>

      <!-- 文件夹选择 -->
      <div class="input-group">
        <label class="input-label">Select Folder</label>
        <div class="popup-folder-list">
          <!-- Home 选项 -->
          <div class="folder-group">
            <div class="folder-item home-folder" @click="selectFolder(null)">
              <div class="folder-icon">
                <i class="el-icon-house"></i>
              </div>
              <div class="folder-info">
                <h4>Home (All Notes)</h4>
                <p>Move note to main page</p>
              </div>
              <div class="check-icon" v-if="!note.folder_id">
                <i class="el-icon-check"></i>
              </div>
            </div>
          </div>
          
          <!-- 现有文件夹 -->
          <div class="folder-group" v-if="folders.length > 0">
            <div class="folder-group-title">Your Folders</div>
            <div v-for="folder in folders" :key="folder.folder_id"
              class="folder-item" 
              :class="{'current-folder': note.folder_id === folder.folder_id}"
              @click="selectFolder(folder)">
              <div class="folder-icon">
                <i class="el-icon-folder"></i>
              </div>
              <div class="folder-info">
                <h4>{{ folder.folder_name }}</h4>
                <p>{{ folder.note_count }} note{{ folder.note_count !== 1 ? 's' : '' }}</p>
              </div>
              <div class="check-icon" v-if="note.folder_id === folder.folder_id">
                <i class="el-icon-check"></i>
              </div>
            </div>
          </div>
          
          <!-- 创建新文件夹 -->
          <div class="folder-group">
            <div class="folder-item new-folder" @click="showCreateFolder">
              <div class="folder-icon">
                <i class="el-icon-plus"></i>
              </div>
              <div class="folder-info">
                <h4>Create New Folder</h4>
                <p>Add a new folder for organization</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PopupNoteMove',
  props: {
    note: {
      type: Object,
      required: false,
      default: null
    },
    folders: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      isUpdating: false
    }
  },
  methods: {
    openModal() {
      if (!this.note) return
      this.visible = true
    },
    closeModal() {
      this.visible = false
    },
    showCreateFolder() {
      this.$emit('showCreateFolder')
    },
    async selectFolder(folder) {
      if (!this.note) return
      
      // Skip if selecting the same folder
      if ((folder && folder.folder_id === this.note.folder_id) || 
          (!folder && !this.note.folder_id)) {
        return
      }

      try {
        this.isUpdating = true
        const response = await this.$axios.post('/api/updatenotefolder/', {
          note_id: this.note.noteid,
          folder_id: folder ? folder.folder_id : null,
          subid: this.$cookies.get('subid')
        })

        if (response.data.code === 200) {
          this.$message.success(folder ? 'Note moved to folder' : 'Note moved to home page')

          // 确保发送完整的更新信息
          this.$emit('noteMoved', {
            note: {
              ...this.note,
              folder_id: folder ? folder.folder_id : null
            },
            // 从响应中获取更新后的文件夹信息，包含最新的笔记数量
            folder: response.data.data.folder
          })

          this.closeModal()
        } else {
          this.$message.error(response.data.messages)
        }
      } catch (error) {
        console.error('Error updating note folder:', error)
        this.$message.error('Failed to move note')
      } finally {
        this.isUpdating = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  animation: fadeIn 0.3s ease, slideIn 0.3s ease;
  max-height: 80vh;
  overflow-y: auto;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(20px);
  }
  to {
    transform: translateY(0);
  }
}

.close-button {
  position: absolute;
  right: 20px;
  top: 20px;
  background: none;
  border: none;
  color: #666666;
  font-size: 24px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-button:hover {
  opacity: 1;
  background: #f5f5f5;
  color: #333333;
}

.close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-title {
  color: #1a1a1a;
  font-size: 24px;
  margin: 0 0 28px 0;
  font-weight: 600;
}

.input-group {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  color: #2c3e50;
  font-size: 15px;
  margin-bottom: 12px;
  font-weight: 500;
}

.popup-folder-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding: 2px 5px 2px 2px;
}

.folder-list::-webkit-scrollbar {
  width: 6px;
}

.folder-list::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.folder-list::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

.folder-list::-webkit-scrollbar-thumb:hover {
  background: #d0d0d0;
}

.folder-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: 2px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
}

.folder-item:hover {
  background: #f0f2f5;
  border-color: #d0d0d0;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.current-folder {
  background-color: #ECF5FF;
  border-color: #A0CFFF;
  box-shadow: 0 2px 8px rgba(160, 207, 255, 0.25);
}

.home-folder {
  background-color: #f8f9fa;
  border-color: #e0e0e0;
  border-bottom: 1px solid #e8e8e8;
}

.new-folder {
  background-color: #f0f7ff;
  border-color: #d0e6ff;
  margin-top: 8px;
  position: relative;
}

.new-folder:before {
  content: '';
  position: absolute;
  top: -12px;
  left: 5%;
  width: 90%;
  height: 1px;
  background-color: #e8e8e8;
}

.folder-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.folder-item:hover .folder-icon {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.home-folder .folder-icon {
  background: #f0f7ff;
}

.new-folder .folder-icon {
  background: #e6f7ff;
}

.folder-icon i {
  font-size: 20px;
  color: #4a90e2;
  transition: all 0.2s ease;
}

.folder-item:hover .folder-icon i {
  color: #2979ff;
}

.home-folder .folder-icon i {
  color: #52c41a;
}

.new-folder .folder-icon i {
  color: #1890ff;
}

.home-folder:hover .folder-icon i {
  color: #389e0d;
}

.new-folder:hover .folder-icon i {
  color: #096dd9;
}

.folder-info {
  flex: 1;
}

.folder-info h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
  margin-bottom: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.folder-item:hover .folder-info h4 {
  color: #1a1a1a;
}

.folder-info p {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.check-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #4a90e2;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 12px;
}

.check-icon i {
  font-size: 16px;
  color: #fff;
}

/* 添加加载状态 */
.modal-content.loading {
  opacity: 0.7;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  z-index: 5;
}

.loading-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(74, 144, 226, 0.8);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.folder-group-title {
  font-size: 12px;
  color: #999;
  margin: 16px 0 8px 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.folder-group {
  margin-bottom: 18px;
}

.folder-group:last-child {
  margin-bottom: 0;
}
</style> 